import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, skip, take, switchMap, tap } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { IAPIFetchResponse, IAPIListRequestOptions, IAPIListResponse } from '@sondermind/utilities/models-http';
import { ProposalStatus } from '@sondermind/utilities/models-scheduling';
import { ReferralState } from '@sondermind/utilities/models-referrals';

import { Appointment, IAppointmentApiResponse, ISchedulingSagaErrorApiResponse } from '../models/appointment.model';

const SESSIONS_FILTER: Partial<IAPIListRequestOptions> = {
  filter: [
    { key: 'current_and_future_within_video', value: 'true' },
  ],
  page: 'disabled',
  view: 'with_provider_info',
  sort: [
    { key: 'start_time', asc: true }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class AppointmentsHttpService {
  constructor(
    private http: HttpService,
    private configurationService: ConfigurationService,
  ) { }

  private store = new BehaviorSubject<Appointment[]>(null);
  private allPATSessionsByProviderIdStore$ = new BehaviorSubject<Record<number, Appointment[]> | null>(null);

  lazyLoadedStore$ = this.store.pipe(
    switchMap((res) => {
      if (res !== null) {
        return of(res);
      }

      return this.refresh();
    })
  );

  store$ = this.store.asObservable().pipe(
    filter((res) => res !== null)
  );

  // nonPendingAppointments$ excludes events in created aasm state e.g. Pending PAT events
  nonPendingAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => appointment.eventState !== ReferralState.created))
  );

  scheduledAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => appointment.eventState === ReferralState.scheduled))
  );

  pendingPATAppointments$ = this.lazyLoadedStore$.pipe(
    filter((res) => res !== null),
    map((res) => res.filter((appointment) => appointment.proposalStatus === ProposalStatus.PENDING))
  );

  pendingPATSessionsByProviderId$: Observable<Record<number, Appointment>> = this.pendingPATAppointments$.pipe(
    map((appointments) => this.createAppointmentRecordByProviderId(appointments))
  );

  allPATSessionsByProviderId$: Observable<Record<number, Appointment[]>> = this.allPATSessionsByProviderIdStore$.pipe(
    switchMap((appointmentRecord) => (appointmentRecord
      ? of(appointmentRecord)
      : this.index(SESSIONS_FILTER).pipe(
        map((res) => {
          const record = this.createAppointmentsRecordByProviderId(res.data);
          this.allPATSessionsByProviderIdStore$.next(record);
          return record;
        })
      ))
    )
  );

  allSchedulingErrors$: BehaviorSubject<number[] | null> = new BehaviorSubject<number[] | null>(null);

  refresh(): Observable<Appointment[]> {
    this.index(SESSIONS_FILTER).subscribe((res) => {
      this.store.next(res.data);
      this.allPATSessionsByProviderIdStore$.next(this.createAppointmentsRecordByProviderId(res.data));
    });
    return this.store$.pipe(skip(1), take(1));
  }

  index(options: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<Appointment>> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events.json`;
    const params = this.http.generateListParams(options);
    return this.http
      .get<IAPIListResponse<IAppointmentApiResponse>>(url, params)
      .pipe(
        map((resp) => ({ ...resp, data: resp.data.map((res) => new Appointment(res)) })),
        this.http.handleHttpError()
      );
  }

  fetchById(id: number): Observable<Appointment | null> {
    const url = `${this.configurationService.env.svcBase}/scheduling/calendar_events/${id}.json`;
    return this.http
      .get<IAPIFetchResponse<IAppointmentApiResponse>>(url)
      .pipe(
        map((res) => new Appointment(res.data)),
        this.http.handleHttpError()
      );
  }

  /**
   * Fetches the persona provider ids of providers with whom the client scheduled,
   * but an error occured during the scheduling process
   * @param ids Optionally takes in persona provider ids to check for errors with those specific providers
   * @returns a list of all persona provider ids (number)
   */
  fetchSchedulingErrors(ids?: number[]): Observable<number[]> {
    if (this.allSchedulingErrors$.value) {
      return this.allSchedulingErrors$.asObservable();
    }

    let url = `${this.configurationService.env.svcBase}/scheduling/saga_errors`;

    if (ids) {
      const urlparams = ids.map((id) => `persona_provider_ids[]=${id}`);
      url = url.concat(`?${urlparams.join('&')}`);
    }

    return this.http
      .get<IAPIFetchResponse<ISchedulingSagaErrorApiResponse>>(url)
      .pipe(
        tap((res) => this.allSchedulingErrors$.next(res.data.persona_provider_ids)),
        map((res) => res.data.persona_provider_ids),
        this.http.handleHttpErrorStrict()
      );
  }

  private createAppointmentRecordByProviderId(appointments: Appointment[]): Record<number, Appointment> {
    if (!appointments?.length) return {};

    return appointments.reduce((x, item) => {
      const obj = x;
      obj[item.personaProviderId] = item;
      return obj;
    }, {});
  }

  /** Returns an object with provider ids as keys and arrays of the provider's appointments as values */
  private createAppointmentsRecordByProviderId(appointments: Appointment[]): Record<number, Appointment[]> {
    if (!appointments?.length) return {};

    return appointments.reduce((x, item) => {
      const obj = x;

      if (obj[item.personaProviderId]) {
        obj[item.personaProviderId].push(item);
      } else {
        obj[item.personaProviderId] = [item];
      }

      return obj;
    }, {} as Record<number, Appointment[]>);
  }
}
